import { useId, useState, type ReactNode } from 'react';

import {
  Alert,
  Box,
  Button as MuiButton,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  type TextFieldProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import ClaimOptionCard from '@/james-playground/aa-family-claim/ClaimOptionCard';
import {
  say,
  type Ask,
  type Copy,
  type Voice,
} from '@/james-playground/aa-family-claim/claim/asks';
import {
  type Facts,
  type Value,
} from '@/james-playground/aa-family-claim/claim/facts';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { labelOf } from '@/james-playground/aa-family-claim/claim/labels';
import {
  APPROX,
  CARD,
  FIELD,
  GATE,
  GATE_IN_PROMPT,
  HINT,
  OPTION_LABEL,
  PLACEHOLDER,
  QUESTION,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  MONTHS,
  digitsOnly,
  formatProblem,
  prefill,
  shows,
  shownValues,
  str,
  type Approx,
  type Draft,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * The pieces every answer card is made of. Each is a composition of llama
 * and MUI parts, so a card reads as the chat's own furniture: a Card surface,
 * ClaimOptionCard for a pick from a list, Chip with aria-pressed for quick
 * picks, the ChecksCard Yes/No toggle, and small filled TextFields.
 */

// ---- State -----------------------------------------------------------------

/** A card's answers so far, starting from what the claim already has. */
export const useDraft = (
  ask: Ask,
  facts: Facts,
  ids: string[] = ask.entries,
) => {
  const [draft, setDraft] = useState<Draft>(() => prefill(ids, facts));
  const set = (id: string, value: Value) =>
    setDraft((current) => ({ ...current, [id]: value }));
  const visible = (id: string) => shows(id, ask.entries, facts, draft);
  const values = (only: string[] = ids, keepEmpty = false) =>
    shownValues(only, ask.entries, facts, draft, { keepEmpty });
  return { draft, set, setDraft, visible, values };
};

// ---- Copy ------------------------------------------------------------------

/** The question for an entry: the card's own wording, else its short name. */
export const questionOf = (id: string, v: Voice) =>
  QUESTION[id] ? say(QUESTION[id], v) : labelOf(id, v);

export const fieldLabel = (id: string, v: Voice) =>
  FIELD[id] ? say(FIELD[id], v) : labelOf(id, v);

/**
 * The gate's question inside a card, or nothing where the conversation's own
 * question is the gate ("Is she in a hospital, hospice or care home at the
 * moment?"), so the card does not ask it twice.
 */
export const gateQuestion = (
  ask: Ask,
  v: Voice,
): { question?: string; label: string } => {
  const asked = say(ask.prompt, v);
  if (GATE_IN_PROMPT.has(ask.id)) return { label: asked };
  const gate = ask.gate ?? '';
  return { question: GATE[gate] ? say(GATE[gate], v) : asked, label: asked };
};

/** How a registry option reads on screen. */
export const optionText = (id: string, label: string, v: Voice) => {
  const shown = OPTION_LABEL[id]?.[label];
  return shown ? say(shown, v) : label;
};

/** An entry's options, with Yes first, then No, then the rest; "none" last. */
export const optionsOf = (id: string): string[] => {
  const e = entry(id);
  const labels =
    e.answerType === 'yes_no'
      ? ['Yes', 'No']
      : (e.options ?? []).map((o) => o.label);
  const rank = (l: string) =>
    l === 'Yes' ? 0 : l === 'No' ? 1 : /^(None|Neither)/.test(l) ? 3 : 2;
  return [...labels].sort((a, b) => rank(a) - rank(b));
};

export const isExclusive = (label: string) => /^(None|Neither)/.test(label);

// ---- The card --------------------------------------------------------------

/**
 * The card around every ask: its controls, a full-width Continue, and the
 * quiet way out. `actions` replaces Continue where a card has its own pair of
 * answers ("Yes, that's right" and "Change something").
 */
export const CardShell = ({
  label,
  children,
  ready = true,
  onContinue,
  continueLabel = CARD.continue,
  actions,
  onSkip,
}: {
  label: string;
  children?: ReactNode;
  ready?: boolean;
  onContinue?: () => void;
  continueLabel?: string;
  actions?: ReactNode;
  onSkip?: () => void;
}) => (
  <Card variant='padded' component='section' aria-label={label}>
    <Stack spacing={2.5}>
      {children}
      <Stack spacing={1}>
        {actions ?? (
          <MuiButton
            variant='contained'
            fullWidth
            disabled={!ready}
            onClick={onContinue}
          >
            {continueLabel}
          </MuiButton>
        )}
        {onSkip && (
          <MuiButton variant='text' fullWidth onClick={onSkip}>
            {CARD.skip}
          </MuiButton>
        )}
      </Stack>
    </Stack>
  </Card>
);

/** Ids for a card's questions, so each control can be labelled by its own. */
export const useQids = () => {
  const base = useId();
  return (name: string) => `${base}-${name}`;
};

/**
 * A part of a card: a question and the controls that answer it. The control
 * names the question as its label through `qid`.
 */
export const Part = ({
  qid,
  question,
  hint,
  children,
}: {
  qid?: string;
  question?: string;
  hint?: string;
  children?: ReactNode;
}) => (
  <Stack spacing={1.25}>
    {question && (
      <Stack spacing={0.5}>
        <Typography id={qid} variant='body1'>
          {question}
        </Typography>
        {hint && <Typography variant='body2'>{hint}</Typography>}
      </Stack>
    )}
    {children}
  </Stack>
);

/** Several parts, each set off from the last. */
export const Parts = ({ children }: { children?: ReactNode }) => (
  <Stack spacing={3}>{children}</Stack>
);

// ---- Yes and no ------------------------------------------------------------

/**
 * A Yes/No (or the entry's own short options) beside its question, as in
 * ChecksCard. The row wraps, so at a narrow width or large text the buttons
 * drop under the question instead of squeezing it.
 */
export const Toggle = ({
  question,
  label,
  hint,
  options = ['Yes', 'No'],
  display = (o) => o,
  value,
  onChange,
}: {
  /** Shown beside the buttons. Leave it out where the conversation just asked it. */
  question?: string;
  /** The buttons' accessible name when no question is shown. */
  label?: string;
  hint?: string;
  options?: string[];
  display?: (option: string) => string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const id = useId();
  return (
    <Stack
      sx={{ flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 1 }}
      direction='row'
    >
      {(question || hint) && (
        <Stack sx={{ flex: '1 1 12rem', minWidth: 0 }} spacing={0.5}>
          {question && (
            <Typography id={`${id}-q`} variant='body1'>
              {question}
            </Typography>
          )}
          {hint && <Typography variant='body2'>{hint}</Typography>}
        </Stack>
      )}
      <ToggleButtonGroup
        exclusive
        size='small'
        value={value || null}
        aria-labelledby={question ? `${id}-q` : undefined}
        aria-label={question ? undefined : label}
        onChange={(_, next: string | null) => next && onChange(next)}
        sx={{
          flexShrink: 0,
          flexWrap: 'wrap',
          // Full-strength labels: the default unselected grey measured 3:1 on
          // the card (ChecksCard's finding, kept with it).
          '& .MuiToggleButton-root': { color: 'text.primary', px: 1.5 },
          '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover':
            {
              bgcolor: 'primary.main',
              color: 'common.white',
            },
        }}
      >
        {options.map((o) => (
          <ToggleButton key={o} value={o}>
            {display(o)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
};

/** The Yes/No for one registry entry, using its own options where it has them. */
export const EntryToggle = ({
  id,
  v,
  value,
  onChange,
  question,
  label,
  hint,
}: {
  id: string;
  v: Voice;
  value: string;
  onChange: (value: string) => void;
  question?: string;
  label?: string;
  hint?: string;
}) => (
  <Toggle
    question={label && !question ? undefined : (question ?? questionOf(id, v))}
    label={label}
    hint={hint}
    options={optionsOf(id)}
    display={(o) => optionText(id, o, v)}
    value={value}
    onChange={onChange}
  />
);

// ---- Lists of choices ------------------------------------------------------

/** Radio or checkbox cards for a pick from a short list of named things. */
export const ChoiceCards = ({
  multi = false,
  options,
  selected,
  onPick,
  labelledBy,
  label,
}: {
  multi?: boolean;
  options: { value: string; label: string; detail?: ReactNode }[];
  selected: string[];
  onPick: (value: string) => void;
  labelledBy?: string;
  label?: string;
}) => (
  <Stack
    spacing={1.25}
    role={multi ? 'group' : 'radiogroup'}
    aria-labelledby={labelledBy}
    aria-label={labelledBy ? undefined : label}
  >
    {options.map((o) => {
      const on = selected.includes(o.value);
      return (
        <ClaimOptionCard
          key={o.value}
          control={multi ? 'check' : 'radio'}
          selected={on}
          label={o.label}
          onClick={() => onPick(o.value)}
        >
          {on && o.detail}
        </ClaimOptionCard>
      );
    })}
  </Stack>
);

/** Quick picks as chips: one at a time, or several. */
export const Chips = ({
  options,
  selected,
  onPick,
  labelledBy,
  label,
}: {
  options: { value: string; label: string; aria?: string }[];
  selected: string[];
  onPick: (value: string) => void;
  labelledBy?: string;
  label?: string;
}) => (
  <Stack
    sx={{ flexWrap: 'wrap', gap: 1 }}
    direction='row'
    role='group'
    aria-labelledby={labelledBy}
    aria-label={labelledBy ? undefined : label}
  >
    {options.map((o) => {
      const on = selected.includes(o.value);
      return (
        <Chip
          key={o.value}
          label={o.label}
          clickable
          aria-pressed={on}
          aria-label={o.aria}
          variant={on ? 'filled' : 'outlined'}
          color={on ? 'primary' : 'default'}
          onClick={() => onPick(o.value)}
          // Long labels wrap inside the chip instead of running off a narrow
          // screen: the chip grows a line rather than the row overflowing.
          sx={{
            maxWidth: '100%',
            height: 'auto',
            minHeight: 32,
            '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
          }}
        />
      );
    })}
  </Stack>
);

/**
 * A free-text answer with the common ones offered as chips, and a last chip
 * that opens a field for anything else. An answer that matches no chip opens
 * on the field, so a proposal in the family's own words is kept as said.
 */
export const SuggestField = ({
  chips,
  other,
  field,
  value,
  onChange,
  labelledBy,
  label,
}: {
  chips: string[];
  other: string;
  field: string;
  value: string;
  onChange: (value: string) => void;
  labelledBy?: string;
  label?: string;
}) => {
  const [typing, setTyping] = useState(
    Boolean(value) && !chips.includes(value),
  );
  const picked = typing ? other : chips.includes(value) ? value : '';
  return (
    <Stack spacing={1.25}>
      <Chips
        labelledBy={labelledBy}
        label={label}
        options={[...chips, other].map((c) => ({ value: c, label: c }))}
        selected={picked ? [picked] : []}
        onPick={(c) => {
          if (c === other) {
            setTyping(true);
            if (chips.includes(value)) onChange('');
          } else {
            setTyping(false);
            onChange(c);
          }
        }}
      />
      {typing && (
        <Field
          label={field}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Stack>
  );
};

/**
 * A text field with its label above it, as a real label element. llama's
 * filled input reserves a fixed 28px for a label inside it; at 200% text the
 * label outgrows that and covers the answer. Outside, it wraps like any text.
 * `sx` lays out the pair; the field itself is untouched.
 */
export const Field = ({
  label,
  id,
  size = 'small',
  sx,
  ...props
}: Omit<TextFieldProps, 'label'> & { label: string }) => {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Stack spacing={0.75} sx={{ minWidth: 0, ...(sx as object) }}>
      <Typography
        component='label'
        htmlFor={fid}
        variant='body2'
        sx={{ color: 'text.primary' }}
      >
        {label}
      </Typography>
      <TextField
        id={fid}
        size={size}
        fullWidth
        hiddenLabel
        {...props}
        // MUI pads a multiline root as well as its textarea, so the text sat
        // further in than a one-line field's. llama pads the input alone.
        sx={{ '& .MuiInputBase-multiline': { px: 0 } }}
      />
    </Stack>
  );
};

// ---- Numbers ---------------------------------------------------------------

/**
 * A count with a field to type it and buttons either side. Unset until it is
 * touched: pressing More from empty starts at `start`.
 */
export const Count = ({
  label,
  unit,
  value,
  onChange,
  min = 0,
  max = 99,
  step = 1,
  start = 1,
}: {
  /** What is being counted, for the field's accessible name. */
  label: string;
  unit: string;
  value: string;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  step?: number;
  start?: number;
}) => {
  const n = value === '' ? undefined : Number(value);
  const clamp = (x: number) => String(Math.max(min, Math.min(max, x)));
  return (
    <Stack
      sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
      direction='row'
    >
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={0.5}>
        <IconButton
          color='primary'
          aria-label={`${CARD.fewer}: ${label}`}
          disabled={n === undefined || n <= min}
          onClick={() => n !== undefined && onChange(clamp(n - step))}
        >
          <Icon icon='minus' fontSize={18} />
        </IconButton>
        <TextField
          size='small'
          value={value}
          onChange={(e) => {
            const d = digitsOnly(e.target.value).slice(0, 3);
            onChange(d === '' ? '' : clamp(Number(d)));
          }}
          sx={{ width: '4.5rem' }}
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              'aria-label': label,
              style: { textAlign: 'center' },
            },
          }}
        />
        <IconButton
          color='primary'
          aria-label={`${CARD.more}: ${label}`}
          disabled={n !== undefined && n >= max}
          onClick={() => onChange(clamp(n === undefined ? start : n + step))}
        >
          <Icon icon='plus' fontSize={18} />
        </IconButton>
      </Stack>
      <Typography variant='body2' aria-hidden>
        {unit}
      </Typography>
    </Stack>
  );
};

// ---- Dates -----------------------------------------------------------------

/** A rough date: month and year, or only the year. */
export const ApproxDate = ({
  value,
  onChange,
  labelledBy,
  label,
  years = 30,
  today = new Date(),
}: {
  value: Approx;
  onChange: (value: Approx) => void;
  labelledBy?: string;
  label?: string;
  years?: number;
  today?: Date;
}) => {
  const thisYear = today.getFullYear();
  const yearList = Array.from({ length: years + 1 }, (_, i) => thisYear - i);
  return (
    <Stack
      spacing={1}
      role='group'
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : label}
    >
      <Stack sx={{ flexWrap: 'wrap', gap: 1.5 }} direction='row'>
        <Field
          select
          label={APPROX.month}
          value={
            value.noMonth || value.month === null ? '' : String(value.month)
          }
          disabled={value.noMonth}
          onChange={(e) =>
            onChange({
              ...value,
              month: e.target.value ? Number(e.target.value) : null,
            })
          }
          sx={{ flex: '1 1 9rem' }}
          slotProps={{ select: { native: true } }}
        >
          <option value=''>{CARD.choose}</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </Field>
        <Field
          select
          label={APPROX.year}
          value={value.year === null ? '' : String(value.year)}
          onChange={(e) =>
            onChange({
              ...value,
              year: e.target.value ? Number(e.target.value) : null,
            })
          }
          sx={{ flex: '1 1 7rem' }}
          slotProps={{ select: { native: true } }}
        >
          <option value=''>{CARD.choose}</option>
          {yearList.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Field>
      </Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={value.noMonth}
            onChange={(e) =>
              onChange({
                ...value,
                noMonth: e.target.checked,
                month: e.target.checked ? null : value.month,
              })
            }
          />
        }
        label={APPROX.noMonth}
      />
    </Stack>
  );
};

// ---- Typed fields --------------------------------------------------------------

/**
 * One typed answer, shaped by its answer type: an address or long answer runs
 * to several lines, a phone number brings up the phone keypad, an NI number
 * reads in capitals, a date takes DD/MM/YYYY. A format problem shows once the
 * field is left.
 */
export const EntryField = ({
  id,
  v,
  value,
  onChange,
  label,
  hint,
  error,
  multiline: lines,
}: {
  id: string;
  v: Voice;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  hint?: string;
  /** An error the card knows about beyond the format. */
  error?: string;
  /** Overrides the answer type, for an address split into single lines. */
  multiline?: boolean;
}) => {
  const e = entry(id);
  const [left, setLeft] = useState(false);
  const problem = left ? formatProblem(e.answerType, value) : undefined;
  // Long answers start on two lines; a short free-text answer starts on one
  // and grows, so "Two tablets up to four times a day" is never cut off.
  const long = e.answerType === 'address' || e.answerType === 'long_text';
  const multiline = lines ?? (long || e.answerType === 'text');
  const helper =
    error ??
    (problem
      ? CARD.formats[problem]
      : (hint ?? (HINT[id] ? say(HINT[id], v) : undefined)));
  const input: Record<string, unknown> = { autoComplete: 'off' };
  if (e.answerType === 'phone') Object.assign(input, { inputMode: 'tel' });
  if (e.answerType === 'date')
    Object.assign(input, {
      inputMode: 'numeric',
      placeholder: CARD.datePlaceholder,
    });
  if (e.answerType === 'ni_number')
    Object.assign(input, {
      autoCapitalize: 'characters',
      style: { textTransform: 'uppercase' },
    });
  if (PLACEHOLDER[id]) input.placeholder = PLACEHOLDER[id];
  return (
    <Field
      label={label ?? fieldLabel(id, v)}
      value={value}
      type={e.answerType === 'phone' ? 'tel' : 'text'}
      multiline={multiline}
      minRows={multiline ? (long && lines !== false ? 2 : 1) : undefined}
      error={Boolean(error || problem)}
      helperText={helper}
      onChange={(ev) => onChange(ev.target.value)}
      onBlur={() => setLeft(true)}
      slotProps={{ htmlInput: input }}
    />
  );
};

/** Whether a typed value has a format problem the card should stop on. */
export const hasProblem = (id: string, value: Value | undefined) =>
  Boolean(formatProblem(entry(id).answerType, str(value)));

// ---- Small things ---------------------------------------------------------------

/**
 * A line of context in a card, like what a choice means for payment. An
 * Alert, as on the bank sheet, but polite: it appears as the family answers,
 * so it should not interrupt a screen reader mid-sentence.
 */
export const Note = ({
  children,
  tone = 'info',
}: {
  children?: ReactNode;
  tone?: 'info' | 'warning';
}) => (
  <Alert severity={tone} icon={false} role='status'>
    <Stack spacing={0.75}>{children}</Stack>
  </Alert>
);

/** A line saying where the claim's answer came from. */
export const Source = ({ children }: { children?: ReactNode }) => (
  <Stack sx={{ alignItems: 'center' }} direction='row' spacing={0.75}>
    <Box sx={{ display: 'flex', color: 'custom.sunflower.700' }} aria-hidden>
      <Icon icon='sparkles' fontSize={12} />
    </Box>
    <Typography variant='caption'>{children}</Typography>
  </Stack>
);

/** The text button with a plus, for adding a row. */
export const AddButton = ({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <MuiButton
    variant='outlined'
    onClick={onClick}
    disabled={disabled}
    startIcon={<Icon icon='plus' fontSize={14} />}
    sx={{ alignSelf: 'flex-start' }}
  >
    {label}
  </MuiButton>
);

export type { Copy };
